import type { Script } from "@unhead/vue";
import type { Info, Locale, Site, Stories } from "@/types";

export const getSite = () => useNuxtData("site").data as Ref<Site>;
export const getInfo = () => useNuxtData("info").data as Ref<Info>;
export const getStories = () => useNuxtData("stories").data as Ref<Stories>;
let language: Locale = "ru";
export const getLanguage = () => language;

const redirect = () => {
  if (import.meta.env.DEV) {
    alert("redirect");
    return;
  }

  window.location.pathname = useAppConfig().offersPageUrl;
};

export default async () => {
  const { $logger } = useNuxtApp();

  try {
    const visitPromise = useCreateVisit();

    // Получаем основные данные по текущему URL
    if (import.meta.env.DEV) {
      getSite().value = {
        name: "trust-credit.com",
        alias: "trustcredit",
        locales: ["ru"],
        country: "ru",
      };
    } else {
      await useFetch<Site>("/api/site/by-url", {
        key: "site",
        baseURL: "",
        query: { url: window.location.hostname },
        signal: useAbortTimeout(),
        onRequestError: redirect,
        onResponseError: redirect,
      });
    }

    const site = getSite();
    const alias = site.value.alias;

    // Устанавливаем язык
    if (site.value.locales.length > 1 && !import.meta.env.DEV) {
      const match = location.pathname.match(/^\/(\w+)\//)?.[1];
      language = site.value.locales.find((i) => i === match) ?? site.value.locales[0];
    } else {
      language = site.value.locales[0];
    }

    const token = import.meta.env.DEV ? "18e40e0c76dc7eb2c45cdac2be061490" : (useRoute().query.token as string);

    // Получаем настройки сайта и список сторис
    await Promise.all([
      useFetch<Info>("/api/site/get-info", {
        key: "info",
        baseURL: "",
        query: { alias, language },
        signal: useAbortTimeout(),
        onRequestError: redirect,
        onResponseError: redirect,
      }),
      useFetch<Stories>("/api/stories/story/get-stories", {
        key: "stories",
        baseURL: "",
        query: { siteAlias: alias, token, language },
        signal: useAbortTimeout(),
        onRequestError: redirect,
        onResponseError: redirect,
      }),
    ]);

    const info = getInfo();
    const mainColor = info.value.main_color ?? "#1E51F4";

    // Собираем все сторонние скрипты в массив
    const scripts: Script[] = [];

    let pushService = "";
    const pushRolleradsIds: Record<string, number | undefined> = {
      glav_zaim: 892_266,
      sovetzaim: 892_268,
      petrovichcom: 892_276,
      upetrovicha: 892_273,
      moneykite: 892_269,
      zaim_pro_me: 892_280,
      hot_zaim: 892_271,
      mybank: 892_275,
      a_zaim: 892_277,
      geozaim: 892_267,
      zanachka_ru: 892_274,
      givemoney: 892_272,
      imoneys: 892_270,
      myfinance: 892_279,
      checkmoney: 892_287,
      "my-zaim-ru": 892_278,
      vistazaim: 977_707,
      smszaimnet: 977_708,
      zanizaem: 977_709,
      bankkreditonline: 977_710,
      "moneykite-kz": 893_928,
      "givemoney-kz": 893_926,
      "hotzaim-kz": 893_927,
      "checkmoney-kazahstan": 893_929,
      moneywomen: 994_502,
      beri_credit: 996_433,
    };

    switch (info.value.push_service) {
      case "sendpulse": {
        pushService = info.value.push_sendpulse_code;
        break;
      }
      case "notisend": {
        pushService = info.value.push_notisend_code;
        break;
      }
      case "push4site": {
        pushService = info.value.push_push4site_code;
        break;
      }
    }

    if (pushService) scripts.push({ src: pushService, async: true });

    if (info.value.push_service === "rollerads" && pushRolleradsIds[alias]) {
      scripts.push({
        tagPosition: "bodyClose",
        innerHTML: `(function() {
          const url = new URL(window.location.href);
          const clickID = url.searchParams.get("click_id");
          const sourceID = url.searchParams.get("source_id");

          const s = document.createElement("script");
          s.dataset.cfasync = "false";
          s.src = "https://push-sdk.com/f/sdk.js?z=${pushRolleradsIds[alias]}";
          s.onload = (opts) => {
            opts.zoneID = ${pushRolleradsIds[alias]};
            opts.extClickID = clickID;
            opts.subID1 = sourceID;
            opts.actions.onPermissionGranted = () => {};
            opts.actions.onPermissionDenied = () => {};
            opts.actions.onAlreadySubscribed = () => {};
            opts.actions.onError = () => {};
          };
          document.head.appendChild(s);
        })()`,
      });
    }

    scripts.push({ src: `/api/counter/get-by?alias=${alias}`, async: true });
    if (info.value.dmp_use) scripts.push({ src: "https://dmp.one/sync", async: true });

    // Устанавливаем основной цвет сайта в css-переменную. Подключаем счётчики и скрипты сайта. Устанавливаем заголовок вкладки (title)
    useHead({
      htmlAttrs: {
        style: `--color-main: ${mainColor};`,
      },
      title: `${info.value.title_main} | ${info.value.title}`,
      meta: [
        {
          name: "theme-color",
          content: mainColor,
        },
      ],
      script: import.meta.env.DEV ? [] : scripts,
    });

    await visitPromise;
  } catch (error) {
    $logger.fatalException({ label: "App init error" }, error);
    redirect();
  }
};
